import { orderImportFeatures } from './order-import-features';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BufferedOrderInput, BufferedOrderQuantity, OrderSaveStatus } from './BufferedOrderInput';
import { createPendingEdits } from './pending-order-edits';
import { BulkOrderAction, OrderPreviewPopover } from './OrderPreviewToolbarOverlays';
import { OrderPreviewTitleHint } from './OrderPreviewTitleHint';
import { useOrderImportDraft } from './use-order-import-draft';
import { Dialog } from '../../../../../components/ui/Dialog';
import { Button } from '../../../../../components/ui/Button';
import { Radio } from '../../../../../components/ui/Radio';
import { Checkbox } from '../../../../../components/ui/Checkbox';
import { DatePicker } from '../../../../../components/ui/DatePicker';
import { Drawer } from '../../../../../components/ui/Drawer';
import { DropdownMenu, type DropdownMenuItem } from '../../../../../components/ui/DropdownMenu';
import { Icon } from '../../../../../components/ui/Icon';
import { Link } from '../../../../../components/ui/Link';
import { Input } from '../../../../../components/ui/Input';
import { Pagination } from '../../../../../components/ui/Pagination';
import { Popover } from '../../../../../components/ui/Popover';
import { Select, type SelectOption } from '../../../../../components/ui/Select';
import { Tabs, type TabItem } from '../../../../../components/ui/Tabs';
import { emptyHorizontalScrollMetrics, HorizontalScrollbar, readHorizontalScrollMetrics, useHorizontalScroll } from '../../../../../components/ui/Table';
import type { MessageType } from '../../../../../components/ui/Message';
import { FieldHelp } from '../../../../../components/ui/FieldHelp';
import { campusOptions, chargeTypeOptions, getItemDiscount, getOrderTotal, groupIssueList, projectOptions, withIssues, type GroupIssue, type ItemIssueField, type PreviewErrorCategory, type PreviewItem, type PreviewOrder } from './order-preview-fixtures';
import styles from './OrderPreviewDialog.module.css';

interface OrderPreviewDialogProps {
  draftKey: string;
  fileName: string;
  /** 关闭预览；`confirmed` 为 true 表示用户已确认导入，调用方据此清空文件态。 */
  onClose: (confirmed?: boolean) => void;
  onNotice: (message: string, type?: MessageType) => void;
  open: boolean;
}

const categories: readonly PreviewErrorCategory[] = ['学员信息', '项目匹配', '校区/授权', '数据格式/冲突'];
const categoryLabels: Record<PreviewErrorCategory, string> = {
  '学员信息': '学员信息',
  '项目匹配': '项目匹配',
  '校区/授权': '校区 / 授权',
  '数据格式/冲突': '数据格式 / 冲突',
};
const pageSizeOptions = [50, 100, 200];
const expansionMenuItems: readonly DropdownMenuItem[] = [
  { id: 'collapse-all', label: '全部折叠' },
  { id: 'expand-all', label: '全部展开' },
  { id: 'expand-errors', label: '仅展开错误' },
];
const sortMenuItems: readonly DropdownMenuItem[] = [
  { id: 'original', label: '默认顺序' },
  { id: 'amount-desc', label: '应交金额 ↓' },
  { id: 'amount-asc', label: '应交金额 ↑' },
];
const optionalColumns = [
  { id: 'category', label: '项目类型' },
  { id: 'quantity', label: '购买数量' },
  { id: 'unitPrice', label: '系统标准价 · 自动' },
  { id: 'dueAmount', label: '应交金额（¥）' },
  { id: 'discount', label: '直减优惠 · 自动' },
  { id: 'expiresAt', label: '有效期至' },
  { id: 'chargeType', label: '收费类型' },
] as const;
type OptionalColumn = typeof optionalColumns[number]['id'];
const initialVisibleColumns = optionalColumns.map(({ id }) => id);

/** 明细行的项目类型与收费类型都是枚举：报错要能改，所以行内是下拉而不是文本。 */
const categoryOptions: readonly SelectOption[] = ['课程', '物品'].map((label) => ({ label, value: label }));
const chargeOptions = chargeTypeOptions.map((value) => ({ label: value, value }));

/* 业绩归属人、销售角色在系统里是选人/选角色，不是自由文本，所以行内一律用下拉而不是输入框。 */
const ownerOptions: readonly SelectOption[] = ['王思远', '李文静', '陈可欣', '周敏', '赵一鸣'].map((name) => ({ label: name, value: name }));
const roleOptions: readonly SelectOption[] = ['课程顾问', '主销售', '副销售', '班主任'].map((label) => ({ label, value: label }));
const campusSelectOptions = campusOptions.map((value) => ({ label: value, value }));
const allOption: SelectOption = { label: '全部', value: '' };

/*
 * 校区下拉以系统校区为准，但当前值不在名单里（例如文件里填的「待匹配校区」）也必须带出来：
 * Select 找不到匹配项会显示成 placeholder，用户就看不到自己导入的到底是什么、也就无从改起。
 */
const projectSelectOptions = projectOptions.map((value) => ({ label: value, value }));

function campusOptionsWith(current: string, recommended?: string): SelectOption[] {
  const options = recommended && current !== recommended
    ? campusSelectOptions.map((option) => option.value === recommended ? { ...option, description: '学员所属校区 · 推荐' } : option)
    : campusSelectOptions;
  return current && !campusOptions.includes(current) ? [{ label: current, value: current, description: '导入值 · 请重新选择' }, ...options] : options;
}

/*
 * 项目名同理：file 里带来的名字（「寒假数学提升班（旧名称）」）即使系统里没有也必须出现在选项里，
 * 否则下拉显示成 placeholder，用户看不到自己导入的到底是什么、也就没法改成正确的那个。
 */
function projectOptionsWith(current: string): SelectOption[] {
  return current && !projectOptions.includes(current) ? [{ label: current, value: current }, ...projectSelectOptions] : projectSelectOptions;
}

type GroupIssueField = GroupIssue['field'];

/*
 * 每个可编辑字段的 DOM id：报错入口点一下就锁定到它。
 * 组级 id 挂在 input 上，明细级 id 挂在单元格 td 上（一个格子里可能是 input 也可能是 Select 的按钮），
 * 所以这里两种都要认：先看自己是不是输入框，不是就在里面找第一个可聚焦控件。
 * 找的时候**先找 input 再找 button**：数量的步进器里减号按钮排在输入框前面，只按文档顺序取第一个
 * 就会聚焦到减号上，用户点「处理」过来还得再按一次 Tab 才能改数字。
 */
function fieldId(scope: string, field: string) {
  return `preview-${scope}-${field}`;
}

function focusIssueField(scope: string, field: string) {
  const host = document.getElementById(fieldId(scope, field));
  if (!host) return;
  /*
   * 候选按文档顺序：自己、然后是格子里的 input，最后才是 button。顺序不能只按文档位置取第一个——
   * ①数量的步进器里减号按钮排在输入框前面，取第一个会聚焦到减号上，用户还得再按一次 Tab；
   * ②Select 的根里带着一个给表单用的隐藏 input，它同样排在触发器前面，聚焦它毫无反应。
   * 所以只认真实可见的控件（隐藏元素没有 offsetParent）。
   */
  const controls: (HTMLInputElement | HTMLButtonElement)[] = [
    ...(host instanceof HTMLInputElement || host instanceof HTMLButtonElement ? [host] : []),
    ...Array.from(host.querySelectorAll<HTMLInputElement>('input')),
    ...Array.from(host.querySelectorAll<HTMLButtonElement>('button')),
  ];
  const control = controls.find((node) => node.offsetParent !== null && !node.disabled && node.getAttribute('aria-hidden') !== 'true');
  if (!control) return;
  control.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  control.focus();
  // 输入框顺带全选旧值，方便直接改写；下拉没有文本可全选，聚焦即可。
  if (control instanceof HTMLInputElement) control.select();
}

/*
 * 一笔订单的报错拉平成「当前 / 下一项」的队列：组级（学员信息）在前，明细按行序在后。
 * 顺序在这里定一次，组行下方的提示条（一次只显示队首）和校验列的气泡（全部列出）
 * 都消费这份列表，两处的「第 1 项」才是同一条。
 */
interface PreviewIssueEntry {
  /** 报错归属哪个可编辑字段，同时也是 fieldId 的后半段。 */
  readonly field: string;
  /** 明细级报错才有：这一项要点开组才能改，所以定位前得先展开。 */
  readonly itemId?: string;
  readonly key: string;
  readonly message: string;
  /** fieldId 的作用域：组级是订单 id，明细级是明细 id。 */
  readonly scope: string;
  /** 报错出在哪儿——学员信息行，还是第几行原表明细。 */
  readonly source: string;
}

function orderIssueEntries(order: PreviewOrder): readonly PreviewIssueEntry[] {
  const entries: PreviewIssueEntry[] = groupIssueList(order).map((issue) => ({
    field: issue.field,
    key: `${order.id}:${issue.code}`,
    message: issue.message,
    scope: order.id,
    source: '学员信息',
  }));
  for (const item of order.items) {
    for (const issue of item.issues) {
      entries.push({
        field: issue.field,
        itemId: item.id,
        key: `${item.id}:${issue.code}`,
        message: issue.message,
        scope: item.id,
        source: `原表行 ${item.originalRow}`,
      });
    }
  }
  return entries;
}

function comma(value: number) {
  return new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(value);
}

function numericValue(value: string) {
  return Number(value.replaceAll(',', '')) || 0;
}

export function OrderPreviewDialog({ draftKey, fileName, onClose, onNotice, open }: OrderPreviewDialogProps) {
  const { orders, setOrders, state: saveState, savedAt, saveNow, retry } = useOrderImportDraft(draftKey);
  const [closeFailed, setCloseFailed] = useState(false);
  const [view, setView] = useState<'all' | 'performance' | 'complete' | 'errors'>('all');
  const [categoryFilter, setCategoryFilter] = useState<PreviewErrorCategory | ''>('');
  const [search, setSearch] = useState('');
  const [visibleColumns, setVisibleColumns] = useState<readonly OptionalColumn[]>(initialVisibleColumns);
  const [campusFilter, setCampusFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [performanceCampusFilter, setPerformanceCampusFilter] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [collapsed, setCollapsed] = useState<readonly string[]>(() => orders.map((order) => order.id));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sortKey, setSortKey] = useState<'original' | 'amount-asc' | 'amount-desc'>('original');
  const [pendingEdits] = useState(createPendingEdits);
  function flushAndSave() {
    pendingEdits.flush();
    return saveNow();
  }
  useEffect(() => {
    const flush = () => { pendingEdits.flush(); saveNow(); };
    const onHide = () => { if (document.hidden) flush(); };
    window.addEventListener('pagehide', flush, true);
    document.addEventListener('visibilitychange', onHide, true);
    return () => {
      window.removeEventListener('pagehide', flush, true);
      document.removeEventListener('visibilitychange', onHide, true);
    };
  }, [pendingEdits, saveNow]);

  const showsColumn = (column: OptionalColumn) => visibleColumns.includes(column);
  const columnCount = visibleColumns.length + 4;

  // 「X 笔待修正」数的是有报错的订单笔数，不是报错条数——一笔订单可能错好几个字段。
  const errorTotal = orders.filter((order) => order.errors.length > 0).length;
  const completeTotal = orders.filter((order) => order.owner && order.role && order.commission && order.performanceCampus).length;
  const viewTabLabel = (label: string, count: number, tone?: 'error') => <span className={styles.viewTabLabel}>{label}<span className={styles.viewTabCount} data-single-digit={count >= 0 && count < 10 || undefined} data-tone={tone}>{count.toLocaleString('zh-CN')}</span></span>;
  const viewTabs: readonly TabItem[] = [
    { id: 'all', label: viewTabLabel('全部订单', orders.length) },
    { id: 'performance', label: viewTabLabel('待完善业绩', orders.length - completeTotal) },
    { id: 'complete', label: viewTabLabel('业绩已完善', completeTotal) },
    { id: 'errors', label: viewTabLabel('数据待修正', errorTotal, 'error') },
  ];
  const categorizedCounts = useMemo(() => Object.fromEntries(categories.map((category) => [
    category,
    orders.reduce((count, order) => count + order.errors.filter((error) => error === category).length, 0),
  ])) as Record<PreviewErrorCategory, number>, [orders]);
  const activeFilterCount = [categoryFilter, search.trim(), campusFilter, ownerFilter, performanceCampusFilter].filter(Boolean).length;
  const filtered = useMemo(() => {
    const normalized = search.trim().toLocaleLowerCase();
    const base = orders.filter((order) => {
      if (view === 'performance' && order.owner && order.role && order.commission && order.performanceCampus) return false;
      if (view === 'complete' && !(order.owner && order.role && order.commission && order.performanceCampus)) return false;
      if (view === 'errors' && order.errors.length === 0) return false;
      if (categoryFilter && !order.errors.includes(categoryFilter)) return false;
      if (campusFilter && order.campus !== campusFilter) return false;
      if (ownerFilter && order.owner !== ownerFilter) return false;
      if (performanceCampusFilter && order.performanceCampus !== performanceCampusFilter) return false;
      return !normalized || [order.name, order.mobile, order.campus, ...order.items.map((item) => item.project)].some((text) => text.toLocaleLowerCase().includes(normalized));
    });
    // 排序在筛选之后做：原序保留 buildPreviewOrders 的输出顺序；金额升降序都是稳定比较。
    const sorted = [...base];
    if (sortKey === 'amount-asc') sorted.sort((a, b) => getOrderTotal(a) - getOrderTotal(b));
    else if (sortKey === 'amount-desc') sorted.sort((a, b) => getOrderTotal(b) - getOrderTotal(a));
    return sorted;
  }, [campusFilter, categoryFilter, orders, ownerFilter, performanceCampusFilter, search, sortKey, view]);
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);
  const pageKeys = visible.map((order) => order.id);
  const selectedOnPage = pageKeys.filter((id) => selected.includes(id)).length;
  const allPageSelected = pageKeys.length > 0 && selectedOnPage === pageKeys.length;
  const firstDetailRow = visible[0]?.items[0]?.originalRow ?? 0;
  /*
   * 横向滚动条与共享 Table 用同一套自绘实现：原生条在 macOS 上是 overlay——滚完就隐、不占布局，
   * 表格右边那 500px 内容毫无提示；这里把原生条藏掉，改用一条常驻、可拖可点的横条。
   */
  const { ref: viewportRef, metrics: viewportScroll, maxScrollLeft, onScroll, onWheel, setScrollLeft } = useHorizontalScroll(
    readHorizontalScrollMetrics,
    emptyHorizontalScrollMetrics,
    [visible.length, page, pageSize, visibleColumns.length],
  );
  const workareaRef = useRef<HTMLDivElement | null>(null);
  const attachWorkarea = useCallback((node: HTMLDivElement | null) => {
    workareaRef.current = node;
    viewportRef(node);
  }, [viewportRef]);
  useEffect(() => {
    // A new result set starts at its tools and first row, without resetting horizontal position.
    if (workareaRef.current) workareaRef.current.scrollTop = 0;
  }, [view, categoryFilter, search, campusFilter, ownerFilter, performanceCampusFilter, sortKey, page, pageSize]);

  function updateOrder(orderId: string, change: Partial<Pick<PreviewOrder, 'name' | 'mobile' | 'campus' | 'performanceCampus' | 'owner' | 'role' | 'commission' | 'note'>>) {
    setOrders((current) => current.map((order) => {
      if (order.id !== orderId) return order;
      // 报错随改值重算：改对了消失，改成另一种错就换成新的那条文案。
      // 校区一变，明细里的「项目未授权」也要跟着重判，所以走 withIssues 而不是只算组级。
      return withIssues({ ...order, ...change });
    }));
  }

  function updateItem(orderId: string, itemId: string, change: Partial<Pick<PreviewItem, 'project' | 'category' | 'quantity' | 'dueAmount' | 'expiresAt' | 'chargeType'>>) {
    setOrders((current) => current.map((order) => {
      if (order.id !== orderId) return order;
      const items = order.items.map((item) => (item.id === itemId ? { ...item, ...change } : item));
      return withIssues({ ...order, items });
    }));
  }

  /*
   * 气泡里的「处理」：先收起气泡（免得它挡住刚聚焦过去的那列），明细级报错再展开组，
   * 最后把焦点送进出错的字段并全选旧值。顺序不能调换：组折叠时那个控件还不在 DOM 里，
   * 同步 focus() 会落空，所以放到下一个宏任务，等 React 把展开后的行提交上去再找元素。
   */
  function handleIssueFix(order: PreviewOrder, entry: PreviewIssueEntry, close?: () => void) {
    // 气泡里的处理带着 close 进来；组行下方红条的定位入口不在气泡里，没有可关的东西。
    close?.();
    if (entry.itemId) setCollapsed((current) => current.filter((id) => id !== order.id));
    if (optionalColumns.some(({ id }) => id === entry.field)) {
      const column = entry.field as OptionalColumn;
      setVisibleColumns((current) => current.includes(column) ? current : [...current, column]);
    }
    setTimeout(() => {
      focusIssueField(entry.scope, entry.field);
      const host = document.getElementById(fieldId(entry.scope, entry.field));
      if (!host) return;
      // 在控件已有的外边框上闪烁，输入元素本身没有边框，不能在里面再画一圈。
      const target = host instanceof HTMLInputElement
        ? host.closest<HTMLElement>(`.${styles.fieldValue}`) ?? host
        : host.tagName === 'TD'
          ? host.querySelector<HTMLElement>(`.${styles.cellSelect} > button, .${styles.cellInput}, .${styles.cellNumber}, .${styles.cellDate}`) ?? host
          : host;
      target.classList.remove(styles.issueTarget);
      // 重启定位动效，让重复点击同一错误时也有反馈。
      void target.offsetWidth;
      target.classList.add(styles.issueTarget);
      target.addEventListener('animationend', () => target.classList.remove(styles.issueTarget), { once: true });
    }, 0);
  }

  /** 气泡内容：每条报错一行「来源 + 文案 + 处理」。组行与明细行共用，区别只在 source 写什么。 */
  function issueLines(order: PreviewOrder, entries: readonly PreviewIssueEntry[], close: () => void) {
    return <div className={styles.issueLines}>{entries.map((entry) => <div className={styles.issueLine} key={entry.key}>
      <span className={styles.issueSource}>{entry.source}</span>
      <span className={styles.issueMessage}>{entry.message}</span>
      <button className={styles.issueFix} onClick={() => handleIssueFix(order, entry, close)} type="button">处理</button>
    </div>)}</div>;
  }

  function changeView(next: typeof view) {
    pendingEdits.flush();
    setView(next);
    setCategoryFilter('');
    setPage(1);
  }

  function applyBulk(patch: Partial<PreviewOrder>) {
    setOrders((current) => current.map((order) => selected.includes(order.id) ? withIssues({ ...order, ...patch }) : order));
    onNotice(`已在本次预览中更新 ${selected.length} 笔订单`, 'success');
  }

  function handleConfirm() {
    if (!flushAndSave()) { setCloseFailed(true); return; }
    onNotice(`演示预览已确认 ${orders.length} 笔订单；未写入真实订单`, 'success');
    setConfirmOpen(false);
    onClose(true);
  }

  const footer = <div className={styles.footer}>
    {/*
      页脚左侧按设计稿读成两组「标签 + 数值」：订单数（含明细数与分组规则说明）与待修正数。
      分组规则说明从原来的常驻提示条改成问号图标上的悬浮气泡——它只在第一次用得着，
      常驻会把整条footer占掉一半。全部通过时待修正数照常显示，只是不带红色。
    */}
    <div className={styles.footerInformation}>
    <div className={styles.footerSummary}>
      <span className={styles.footerItem}>
        <span>{orders.length.toLocaleString('zh-CN')} 笔订单 · {(orders.length * 2).toLocaleString('zh-CN')} 项明细</span>
        <FieldHelp content="姓名、手机号、校区相同归为一组，生成一笔订单。学员信息在组标题中修改，明细只展示课程与物品信息。标准价取自系统项目，直减优惠自动计算。" label="分组与校验规则说明" />
      </span>
      <span className={styles.footerItem}>
        <span>待修正：</span>
        <span data-error={errorTotal > 0 || undefined}>{errorTotal.toLocaleString('zh-CN')} 条</span>
      </span>
    </div>
    </div>
    <Button onClick={() => onNotice('已重新校验', 'success')} type="default">重新校验</Button>
    <Button disabled={errorTotal > 0} onClick={() => setConfirmOpen(true)} type="primary">确认导入（{orders.length.toLocaleString('zh-CN')}）</Button>
  </div>;

  return <Drawer
    className={styles.dialog}
    footer={footer}
    headerExtra={<OrderSaveStatus pending={pendingEdits} state={saveState} savedAt={savedAt} retry={retry} />}
    layout="right"
    onOpenChange={(next) => { if (!next) { if (flushAndSave()) onClose(); else setCloseFailed(true); } }}
    open={open}
    title={`在线处理：${fileName || '订单导入模板.xls'}`}
    titleSupplement={<OrderPreviewTitleHint />}
    width="calc(100vw - 24px)"
  >
    <div className={styles.preview}>
      <Dialog
        modalLayer="nested"
        onOpenChange={setCloseFailed}
        open={closeFailed}
        title="修改尚未保存"
        footer={<>
          <Button onClick={() => setCloseFailed(false)} type="default">返回编辑</Button>
          <Button onClick={() => { setCloseFailed(false); onClose(); }} type="default">放弃未保存修改</Button>
          <Button onClick={() => { if (flushAndSave()) { setCloseFailed(false); onClose(); } }} type="primary">重试并关闭</Button>
        </>}
      >
        <p>保存失败，当前修改尚未保存。可以返回继续编辑或重试；放弃后仍保留上次成功保存的内容。</p>
      </Dialog>
      <div className={styles.workbench}>
        <main aria-label="待导入订单预览" className={styles.main}>
          {/*
            工具栏按本期范围合并：页签行 → 全选/已选/取消 | 搜索/批量设置/展开折叠/显示字段 → 表格。
            暂缓的筛选、排序、错误分类仍由能力配置控制，恢复后按布局换行。
          */}
          <div className={styles.toolbar}>
            <Tabs ariaLabel="订单数据视图" className={styles.viewTabs} idPrefix="order-preview-view" onChange={(id) => changeView(id as typeof view)} tabs={viewTabs} value={view} />
          </div>
          <div aria-label="订单工具与数据滚动区" className={styles.tableViewport} onScroll={(event) => {
            // Vertical scrolling needs no React state update or table rerender.
            if (event.currentTarget.scrollLeft !== viewportScroll.scrollLeft) onScroll();
          }} onWheel={onWheel} ref={attachWorkarea}>
          <div className={styles.filterBar}>
            <div className={styles.selectionLeft}>
              <span className={styles.selectionAll}>
                <Link disabled={filtered.length === 0} onClick={() => setSelected((current) => [...new Set([...current, ...filtered.map((order) => order.id)])])} type="primary">全选</Link>
                <span className={styles.selectionCount}>（已筛选{filtered.length.toLocaleString('zh-CN')}条）</span>
              </span>
              <span aria-live="polite">已选 {selected.length.toLocaleString('zh-CN')} 条</span>
              {selected.length > 0 ? <Link muted onClick={(event) => { setSelected([]); event.currentTarget.parentElement?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus(); }} type="default">取消选择</Link> : null}
            </div>
            <div className={styles.selectionRight}>
              <Input ariaLabel="搜索学员、手机号或项目" className={styles.search} leadingIcon="search" onValueChange={(value) => { setSearch(value); setPage(1); }} placeholder="搜索学员、手机号、项目" size="default" value={search} />
              {orderImportFeatures.conditionFilters ? <OrderPreviewPopover kind="filter" trigger={(isOpen, toggle) => <Button aria-controls="preview-filter-panel" aria-expanded={isOpen} aria-haspopup="dialog" leadingIcon="filter" onClick={toggle} plain={activeFilterCount > 0} type={activeFilterCount > 0 ? 'primary' : 'default'}>筛选{activeFilterCount > 0 ? `（${activeFilterCount}）` : ''}</Button>}>
                  <div className={styles.filterPanelHeader}>
                    <span className={styles.filterHint}>条件同时满足时显示</span>
                    {activeFilterCount > 0 ? <button className={styles.clearAllFilters} onClick={() => { setCategoryFilter(''); setSearch(''); setCampusFilter(''); setOwnerFilter(''); setPerformanceCampusFilter(''); setPage(1); }} type="button">清空筛选</button> : null}
                  </div>
                  <label className={styles.filterField}>收费校区<Select ariaLabel="筛选收费校区" className={styles.filterSelect} onValueChange={(value) => { setCampusFilter(value); setPage(1); }} options={[allOption, ...campusSelectOptions]} value={campusFilter} /></label>
                  <label className={styles.filterField}>业绩归属人<Select allowClear ariaLabel="筛选业绩归属人" className={styles.filterSelect} onValueChange={(value) => { setOwnerFilter(value); setPage(1); }} options={[allOption, ...ownerOptions]} value={ownerFilter} /></label>
                  <label className={styles.filterField}>业绩归属校区<Select allowClear ariaLabel="筛选业绩归属校区" className={styles.filterSelect} onValueChange={(value) => { setPerformanceCampusFilter(value); setPage(1); }} options={[allOption, ...campusSelectOptions]} value={performanceCampusFilter} /></label>
              </OrderPreviewPopover> : null}
              {orderImportFeatures.sorting ? <DropdownMenu
                ariaLabel="排序方式"
                arrow={false}
                triggerMode="hover"
                items={sortMenuItems}
                onItemSelect={(item) => { setSortKey(item.id as typeof sortKey); setPage(1); }}
                placement="bottomRight"
                trigger={<Button className={styles.menuTrigger} trailingIcon="a-arrow-down" type="default">{sortMenuItems.find((item) => item.id === sortKey)?.label}</Button>}
              /> : null}
              <BulkOrderAction orders={orders} selected={selected} onApply={applyBulk} />
              <DropdownMenu
                ariaLabel="订单折叠与展开选项"
                arrow={false}
                items={expansionMenuItems}
                onItemSelect={(item) => {
                  if (item.id === 'collapse-all') setCollapsed(orders.map((order) => order.id));
                  if (item.id === 'expand-all') setCollapsed([]);
                  if (item.id === 'expand-errors') setCollapsed(orders.filter((order) => order.errors.length === 0).map((order) => order.id));
                }}
                placement="bottomRight"
                trigger={<Button className={styles.menuTrigger} disabled={visible.length === 0} trailingIcon="a-arrow-down" type="default">展开/折叠</Button>}
                triggerMode="hover"
              />
              <OrderPreviewPopover kind="fields" trigger={(isOpen, toggle) => <Button aria-controls="preview-fields-panel" aria-expanded={isOpen} aria-haspopup="dialog" onClick={toggle} type="default">显示字段</Button>}>
                  <Checkbox checked={visibleColumns.length === optionalColumns.length} indeterminate={visibleColumns.length > 0 && visibleColumns.length < optionalColumns.length} label="全选" onCheckedChange={(checked) => setVisibleColumns(checked ? initialVisibleColumns : [])} />
                  <div className={styles.fieldsDivider} />
                  {optionalColumns.map(({ id, label }) => <Checkbox checked={showsColumn(id)} key={id} label={label} onCheckedChange={(checked) => setVisibleColumns((current) => checked ? [...current, id] : current.filter((column) => column !== id))} />)}
              </OrderPreviewPopover>
            </div>
          </div>
          {orderImportFeatures.errorCategoryFilter ? <div className={styles.selectionBar}>
            <div className={styles.errorFilters}>
              {/*
                错误分类使用独立标签：默认浅灰，选中浅红；仍保留单选语义与键盘操作。
                点选即筛选该类错误，再点一次取消——「清空错误筛选」是一键取消的显性入口。
              */}
              {categoryFilter ? <button className={styles.clearFilters} onClick={() => { setCategoryFilter(''); setPage(1); }} type="button">清空错误筛选</button> : <span className={styles.errorFilterLabel}>错误筛选：</span>}
              <div aria-label="错误筛选" className={styles.filterGroup} role="radiogroup">
                {categories.map((category) => <Radio checked={categoryFilter === category} className={styles.errorFilter} key={category} name="order-error-category" onClick={() => { if (categoryFilter === category) { setCategoryFilter(''); setPage(1); } }} onCheckedChange={(checked) => { if (checked) { setCategoryFilter(category); setView('all'); setPage(1); } }} size="small" value={category}><span>{categoryLabels[category]}{categorizedCounts[category]}</span></Radio>)}
              </div>

            </div>
          </div> : null}
          {confirmOpen ? (
            <div className={styles.bulkOverlay} role="dialog" aria-modal="true" aria-label="确认导入">
              <div className={styles.confirmPanel}>
                <div className={styles.bulkHead}>
                  <strong>确认导入</strong>
                  <button aria-label="关闭确认" className={styles.bulkClose} onClick={() => setConfirmOpen(false)} type="button">×</button>
                </div>
                <p className={styles.bulkHint}>此处为原型演示，订单与业绩数据不会写入真实业务系统。导入成功后仍需老师手动推送。</p>
                <div className={styles.bulkFooter}>
                  <Button onClick={() => setConfirmOpen(false)} type="default">返回检查</Button>
                  <Button onClick={handleConfirm} type="primary">确认导入</Button>
                </div>
              </div>
            </div>
          ) : null}
            {/*
              用原生 `<table>` 而不是 `Table` 组件：这里要画「组标题 + 明细」的混合可编辑行，
              `Table` 组件做不了 rowSpan/分组标题。表头选择列与选择条共用同一份 `selected` 状态，
              两处全选 Checkbox 都接同一 handler，行为一致。
            */}
            <table aria-label="待导入订单预览" className={styles.table}>
              <thead><tr>
                <th aria-label="选择" className={styles.selectColumn}><Checkbox ariaLabel="全选当前页订单" checked={allPageSelected} indeterminate={selectedOnPage > 0 && !allPageSelected} onCheckedChange={(checked) => setSelected((current) => checked ? [...new Set([...current, ...pageKeys])] : current.filter((id) => !pageKeys.includes(id)))} /></th>
                {/*
                  校验结果列固定在最后一列：横向滚动时它跟着表体一起横向溢出，但要始终贴在右侧可见，
                  否则滚到右边就看不见「到底哪条错了」。列顺序只动这一次，后面新增业务列插到它之前。
                */}
                <th data-column="originalRow">原表行</th><th data-column="project">课程 / 物品名称</th>
                {optionalColumns.map(({ id, label }) => showsColumn(id) ? <th data-column={id} key={id}>{label}</th> : null)}
                <th data-column="verdict">校验结果 / 修正建议</th>
              </tr></thead>
              <tbody>
                {visible.map((order) => {
                  const issues = groupIssueList(order);
                  const issueOf = (field: GroupIssueField) => issues.find((issue) => issue.field === field);
                  // 组行末列与下方提示条共用同一份队列：末列给总数，提示条给队首。
                  const entries = orderIssueEntries(order);
                  const current = entries[0];
                  const issueTotal = entries.length;
                  return <Fragment key={order.id}>
                    {/* 组标题行占满整行宽度：选择/折叠单独一列，中间是学员与业绩字段区，最后一列落到校验列上，跟明细行同一列右对齐。 */}
                    <tr className={styles.groupRow}>
                      <td className={styles.groupToggle}>
                        <Checkbox ariaLabel={`选择${order.name} · ${order.mobile} · ${order.campus}`} checked={selected.includes(order.id)} onCheckedChange={(checked) => setSelected((current) => checked ? [...current, order.id] : current.filter((id) => id !== order.id))} />
                        <button aria-expanded={!collapsed.includes(order.id)} aria-label={`${collapsed.includes(order.id) ? '展开' : '折叠'}${order.name} · ${order.mobile} · ${order.campus}`} className={styles.collapse} onClick={() => setCollapsed((current) => current.includes(order.id) ? current.filter((id) => id !== order.id) : [...current, order.id])} type="button"><i aria-hidden="true" className={styles.sideIcon}><Icon name={collapsed.includes(order.id) ? 'caret-right' : 'caret-bottom'} size="sm" /></i></button>
                      </td>
                      <td className={styles.groupMain} colSpan={visibleColumns.length + 2}>
                        <div className={styles.groupContent}>
                          {/* 字段平时读成「标签 + 值」的纯文本，鼠标移上去才显形为输入框（.fieldValue 控制），
                              报错时才给红框——三态由 CSS 决定，DOM 始终是一个原生 input，改值不用切模式。 */}
                          <label className={styles.identityField}>学员姓名<BufferedOrderInput editKey={`${order.id}:name`} pending={pendingEdits} ariaLabel={`${order.name} 学员姓名`} className={styles.fieldValue} error={Boolean(issueOf('name'))} id={fieldId(order.id, 'name')} onCommit={(value) => { updateOrder(order.id, { name: value }); saveNow(); }} placeholder="点击填写" size="small" value={order.name} /></label>
                          <label className={styles.identityField}>手机号<BufferedOrderInput editKey={`${order.id}:mobile`} pending={pendingEdits} ariaLabel={`${order.name} 手机号`} className={styles.fieldValue} error={Boolean(issueOf('mobile'))} id={fieldId(order.id, 'mobile')} onCommit={(value) => { updateOrder(order.id, { mobile: value }); saveNow(); }} placeholder="点击填写" size="small" value={order.mobile} /></label>
                          {/* 收费校区也只有系统里登记过的那几个，输错了就是「校区不存在」，所以给下拉。 */}
                          <label className={styles.identityField}>收费校区<Select ariaLabel={`${order.name} 收费校区`} className={styles.pickerValue} error={Boolean(issueOf('campus'))} id={fieldId(order.id, 'campus')} onValueChange={(value) => updateOrder(order.id, { campus: value })} options={campusOptionsWith(order.campus, order.expectedCampus)} placeholder="选择校区" size="small" value={order.campus} /></label>
                          <span className={styles.itemCount}>{order.items.length} 条</span>
                          {/* 这三个在系统里是选择项（选人 / 选角色 / 选校区），不是自由文本，所以用下拉。
                              纯文本态与输入框字段保持一致：平时无框，hover 或展开才显形（.pickerValue 控制）。 */}
                          <label className={styles.orderField}>业绩归属人<Select allowClear ariaLabel={`${order.name} 业绩归属人`} className={styles.pickerValue} onValueChange={(value) => updateOrder(order.id, { owner: value })} options={ownerOptions} placeholder="选择人员" size="small" value={order.owner} /></label>
                          <label className={styles.orderField}>销售角色<Select allowClear ariaLabel={`${order.name} 销售角色`} className={styles.pickerValue} onValueChange={(value) => updateOrder(order.id, { role: value })} options={roleOptions} placeholder="选择角色" size="small" value={order.role} /></label>
                          <label className={styles.orderField}>提成比例（%）<BufferedOrderInput ariaLabel={`${order.name} 提成比例`} className={styles.fieldValue} editKey={`${order.id}:commission`} pending={pendingEdits} onCommit={(value) => { updateOrder(order.id, { commission: value }); saveNow(); }} placeholder="—" size="small" suffix="%" value={order.commission} /></label>
                          <label className={styles.orderField}>业绩归属校区<Select allowClear ariaLabel={`${order.name} 业绩归属校区`} className={styles.pickerValue} onValueChange={(value) => updateOrder(order.id, { performanceCampus: value })} options={campusOptionsWith(order.performanceCampus)} placeholder="选择校区" size="small" value={order.performanceCampus} /></label>
                          <label className={styles.orderField}>内部备注<BufferedOrderInput editKey={`${order.id}:note`} pending={pendingEdits} ariaLabel={`${order.name} 内部备注`} className={styles.fieldValue} onCommit={(value) => { updateOrder(order.id, { note: value }); saveNow(); }} placeholder="填写备注" size="small" value={order.note} /></label>
                          <div className={styles.orderTotal}><span>应交</span><strong>¥{comma(getOrderTotal(order))}</strong></div>
                        </div>
                      </td>
                      <td>{issueTotal ? <Popover
                        className={styles.issuePopover}
                        content={({ close }) => issueLines(order, entries, close)}
                        openOnHover
                        side="left"
                        title={`${issueTotal} 项待修正`}
                      >
                        <button className={styles.verdictState} data-error type="button"><i aria-hidden="true" className={styles.sideIcon}><Icon name="warning-filled" size="xs" /></i>{issueTotal} 项待修正</button>
                      </Popover> : <span className={styles.verdictState} data-success><i aria-hidden="true" className={styles.sideIcon}><Icon name="circle-check-filled" size="xs" /></i>通过</span>}</td>
                    </tr>
                    {/*
                      组行下方的提示条一次只给队首那一条：整单的报错按「学员信息 → 明细」排成队列，
                      改对了当前那条就重算队列、自动顶上下一条——不把七八条错误一起铺在行间让人挑。
                      完整列表在右侧校验列的气泡里，两处共用同一份顺序。

                      整条提示是可点击按钮；鼠标落在条内任意位置（含左侧缩进）都跳到出错字段。
                      悬停或键盘聚焦时给文案下划线反馈，不再额外显示「处理」字样。
                    */}
                    {current ? <tr className={styles.issueBar}>
                      <td colSpan={columnCount}>
                        <button className={styles.issueBarNote} onClick={() => handleIssueFix(order, current)} type="button">
                          <i aria-hidden="true" className={styles.sideIcon}><Icon name="warning-filled" size="xs" /></i>
                          <span className={styles.issueBarText}>{current.source}：{current.message}。</span>
                          {issueTotal > 1 ? <span className={styles.issueBarIndex}>第 1 / {issueTotal} 项</span> : null}
                        </button>
                      </td>
                    </tr> : null}
                    {!collapsed.includes(order.id) ? order.items.map((item) => {
                      // 同一条明细可能错好几个字段；错误态落在对应的整格，而不是给小控件套红框。
                      const errorOf = (field: ItemIssueField) => item.issues.some((issue) => issue.field === field);
                      return <tr className={styles.itemRow} key={item.id}>
                        <td />
                        <td>{item.originalRow}</td>
                        {/* 项目名从系统已登记的下拉里挑：不正当的名字（旧名称）才改得回去。 */}
                        <td className={styles.editableCell} data-error={errorOf('project') || undefined} id={fieldId(item.id, 'project')}><Select ariaLabel={`${order.name} 原表行${item.originalRow} 课程 / 物品名称`} className={styles.cellSelect} error={errorOf('project')} onValueChange={(value) => updateItem(order.id, item.id, { project: value })} options={projectOptionsWith(item.project)} size="small" value={item.project} /></td>
                        {/* 项目类型与收费类型都是枚举，用下拉而不是文本框：报错「请选择已有选项」也才改得动。 */}
                        {showsColumn('category') ? <td className={styles.editableCell} data-error={errorOf('category') || undefined} id={fieldId(item.id, 'category')}><Select ariaLabel={`${order.name} 原表行${item.originalRow} 项目类型`} className={styles.cellSelect} error={errorOf('category')} onValueChange={(value) => updateItem(order.id, item.id, { category: value as PreviewItem['category'] })} options={categoryOptions} size="small" value={item.category} /></td> : null}
                        {/*
                          数量是数字且有整数要求，用步进器：点加减就能改，不会手输成 2.5 件。
                          加减放在右侧一列（上下两个箭头），并且**只在悬停 / 聚焦时现身**——
                          静态这一格跟同排其他字段一样只是数字，鼠标移上去控件才浮出来，行里不会一列小灰块。
                        */}
                        {showsColumn('quantity') ? <td className={styles.editableCell} data-error={errorOf('quantity') || undefined} id={fieldId(item.id, 'quantity')}><BufferedOrderQuantity editKey={`${item.id}:quantity`} pending={pendingEdits} ariaLabel={`${order.name} 原表行${item.originalRow} 购买数量`} className={styles.cellNumber} onCommit={(value) => { updateItem(order.id, item.id, { quantity: value ?? 0 }); saveNow(); }} placement="right" size="small" step={1} steppersOnHover value={item.quantity} /></td> : null}
                        {showsColumn('unitPrice') ? <td className={styles.readOnly} data-column="unitPrice">{comma(item.unitPrice)}</td> : null}
                        {/*
                          金额是唯一「显示态需要格式化」的可编辑字段：编辑期间在字段内部保留原样输入，
                          停顿后同步数值并保存；失焦才显示格式化金额，避免改写光标和带动整表击键更新。
                        */}
                        {showsColumn('dueAmount') ? <td className={styles.editableCell} data-error={errorOf('dueAmount') || undefined} id={fieldId(item.id, 'dueAmount')}><BufferedOrderInput ariaLabel={`${order.name} 原表行${item.originalRow} 应交金额`} className={styles.cellInput} error={errorOf('dueAmount')} editKey={`${item.id}:amount`} editValue={String(item.dueAmount)} pending={pendingEdits} onCommit={(value) => { updateItem(order.id, item.id, { dueAmount: Math.round(numericValue(value) * 100) / 100 }); saveNow(); }} size="small" value={comma(item.dueAmount)} /></td> : null}
                        {showsColumn('discount') ? <td className={styles.readOnly} data-column="discount">{comma(getItemDiscount(item))}</td> : null}
                        {/* 有效期是日期，用日历面板选：不再手输就没有格式错，也不用纠结分隔符。 */}
                        {showsColumn('expiresAt') ? <td className={styles.editableCell} data-error={errorOf('expiresAt') || undefined} id={fieldId(item.id, 'expiresAt')}><DatePicker ariaLabel={`${order.name} 原表行${item.originalRow} 有效期至`} className={styles.cellDate} error={errorOf('expiresAt')} onValueChange={(value) => updateItem(order.id, item.id, { expiresAt: value })} size="small" value={item.expiresAt} /></td> : null}
                        {showsColumn('chargeType') ? <td className={styles.editableCell} data-error={errorOf('chargeType') || undefined} id={fieldId(item.id, 'chargeType')}><Select ariaLabel={`${order.name} 原表行${item.originalRow} 收费类型`} className={styles.cellSelect} error={errorOf('chargeType')} onValueChange={(value) => updateItem(order.id, item.id, { chargeType: value })} options={chargeOptions} size="small" value={item.chargeType} /></td> : null}
                        {/*
                          明细行不给结论：整单有没有问题上面学员信息行已经说过了（那一行的计数把明细也数进去），
                          这里再写一遍「通过」是同一句话在同一列出现两次，还容易被读成两条独立结论。
                          明细哪里错看字段自己的红框，具体是哪句话在学员信息行的气泡里（标着「原表行 N」）。
                        */}
                        <td />
                      </tr>;
                    }) : null}
                  </Fragment>;
                })}
              </tbody>
            </table>
            {visible.length === 0 ? <div className={styles.empty}>没有符合当前条件的订单</div> : null}
          </div>
          {/* 横条放在滚动容器外面（表体下方、分页上方），跟共享 Table 的位置一致：不占表体高度、不被内容盖住。 */}
          {maxScrollLeft > 0 ? <HorizontalScrollbar className={styles.previewScrollbar} ariaLabel="订单预览横向滚动" metrics={viewportScroll} onScrollLeftChange={setScrollLeft} /> : null}
          <div className={styles.pagination}>
            {/* 统一用 Pagination 组件：总数 → 每页条数 → 页码 → 跳页，顺序与交互跟表格分页一致。 */}
            <Pagination
              align="end"
              ariaLabel="订单分页"
              onChange={(nextPage, nextPageSize) => {
                pendingEdits.flush();
                setPageSize(nextPageSize);
                setPage(nextPage);
              }}
              page={page}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              showQuickJumper
              showSizeChanger
              size="small"
              showTotal={(total) => `共 ${total.toLocaleString('zh-CN')} 笔订单`}
              total={filtered.length}
            />
            {firstDetailRow ? <span className={styles.rangeNote}>当前首条原表行 {firstDetailRow}</span> : null}
          </div>
        </main>
      </div>
    </div>
  </Drawer>;
}
